// src/features/payment/PoliciesTab.tsx
// Payment policies: routing/cascade/3DS behavior for a scope entity.
// Covers: ListPaymentPolicies, CreatePaymentPolicy, GetPaymentPolicy (via list item),
// UpdatePaymentPolicy, DeletePaymentPolicy, ResolvePaymentContext (preview).

import { useEffect, useRef, useState } from 'react';
import { observer } from '@react';
import { usePaymentPaymentPolicy, useI18n } from '@react/hooks';
import {
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { FormDialog, FormRow, TitleInput, AutoGrowTextarea } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { PaymentPolicy } from '@core/types/payment';
import { errMessage } from './format';

interface PolicyForm {
  name: string;
  description: string;
  scope_entity_id: string;
  scope_entity_type: string;
  use_smart_routing: boolean;
  require_3ds: boolean;
}

const EMPTY: PolicyForm = { name: '', description: '', scope_entity_id: '', scope_entity_type: 'tenant', use_smart_routing: true, require_3ds: false };

export const PoliciesTab = observer(() => {
  const store = usePaymentPaymentPolicy();
  const { t } = useI18n();
  const [editing, setEditing] = useState<PaymentPolicy | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<PolicyForm>(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<PaymentPolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ providers: number; routes: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const initialFormRef = useRef<PolicyForm>(EMPTY);
  const descRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    store.ListPaymentPolicies({ pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    initialFormRef.current = EMPTY;
    setError(null);
    setFormOpen(true);
  };
  const openEdit = (p: PaymentPolicy) => {
    const next: PolicyForm = {
      name: p.name ?? '', description: p.description ?? '',
      scope_entity_id: p.scope_entity?.id ?? '', scope_entity_type: p.scope_entity_type ?? 'tenant',
      use_smart_routing: p.use_smart_routing ?? true, require_3ds: p.require_3ds ?? false,
    };
    setEditing(p);
    setForm(next);
    initialFormRef.current = next;
    setError(null);
    setFormOpen(true);
  };

  const canSubmit = !!form.name.trim() && (!!editing || !!form.scope_entity_id.trim());
  const dirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      if (editing?.entity?.id) {
        await store.UpdatePaymentPolicy(editing.entity.id, { policy: { ...editing, name: form.name, description: form.description, use_smart_routing: form.use_smart_routing, require_3ds: form.require_3ds } as any, field_mask: [] } as any);
      } else {
        await store.CreatePaymentPolicy({
          entity: {} as any,
          name: form.name,
          description: form.description,
          scope_entity: { id: form.scope_entity_id } as any,
          scope_entity_type: form.scope_entity_type,
          use_smart_routing: form.use_smart_routing,
          require_3ds: form.require_3ds,
          metadata: {},
        } as any);
      }
      setFormOpen(false);
    } catch (err) {
      setError(errMessage(err, t('payment.policies.saveError')));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    await store.DeletePaymentPolicy(deleteTarget.entity.id);
    setDeleteTarget(null);
  };

  const handlePreview = async (p: PaymentPolicy) => {
    if (!p.scope_entity) return;
    setPreviewFor(p.entity?.id ?? null);
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await store.ResolvePaymentContext({ scope_entity: p.scope_entity } as any);
      setPreview({
        providers: res.available_providers?.providers?.length ?? 0,
        routes: res.active_routes?.routes?.length ?? 0,
      });
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('payment.policies.subtitle')}</p>
        <Button size="sm" onClick={openCreate}>{t('payment.policies.create')}</Button>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('common.name')}</TableHead>
            <TableHead>{t('payment.policies.scope')}</TableHead>
            <TableHead>{t('payment.policies.smartRouting')}</TableHead>
            <TableHead>{t('payment.policies.require3ds')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {store.isLoading ? (
            <TableRow><TableCell colSpan={5} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
          ) : store.items.length === 0 ? (
            <TableEmpty colSpan={5} message={t('payment.policies.empty')} />
          ) : (
            store.items.map((p) => (
              <TableRow key={p.entity?.id}>
                <TableCell>{p.name}</TableCell>
                <TableCell>{p.scope_entity_type || p.scope_entity?.type_name || '--'}</TableCell>
                <TableCell>{p.use_smart_routing ? <Badge variant="success" size="sm">{t('common.yes')}</Badge> : <Badge variant="secondary" size="sm">{t('common.no')}</Badge>}</TableCell>
                <TableCell>{p.require_3ds ? <Badge variant="warning" size="sm">{t('common.yes')}</Badge> : <Badge variant="secondary" size="sm">{t('common.no')}</Badge>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end items-center gap-2">
                    {previewFor === p.entity?.id && preview && (
                      <span className="text-xs text-muted-foreground mr-1">{t('payment.policies.previewResult', { providers: preview.providers, routes: preview.routes })}</span>
                    )}
                    <Button variant="ghost" size="sm" isLoading={previewLoading && previewFor === p.entity?.id} onClick={() => handlePreview(p)}>{t('payment.policies.preview')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>{t('common.edit')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)}>{t('common.remove')}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <FormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={submit}
        submitLabel={editing ? t('common.saveChanges') : t('payment.policies.create')}
        canSubmit={canSubmit}
        isSubmitting={store.isSaving}
        dirty={dirty}
        aria-label={editing ? t('payment.policies.edit') : t('payment.policies.create')}
      >
        <div className="space-y-2 px-3.5 pb-4 pt-2">
          {error && <p className="text-sm text-destructive">{error}</p>}

          <TitleInput
            value={form.name}
            onChange={(v) => setForm((p) => ({ ...p, name: v }))}
            placeholder={t('payment.policies.namePlaceholder')}
            initialFocus
            onEnter={() => descRef.current?.focus()}
            aria-label={t('common.name')}
          />
          <AutoGrowTextarea
            ref={descRef}
            value={form.description}
            onChange={(v) => setForm((p) => ({ ...p, description: v }))}
            placeholder={t('payment.providers.descriptionPlaceholder')}
            minRows={1}
          />

          <div className="space-y-0.5 border-t border-border pt-2">
            {!editing && (
              <>
                <FormRow label={t('payment.policies.scopeEntityId')} labelWidth="sm">
                  <input
                    value={form.scope_entity_id}
                    onChange={(e) => setForm((p) => ({ ...p, scope_entity_id: e.target.value }))}
                    autoCapitalize="off"
                    className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </FormRow>
                <FormRow label={t('payment.policies.scopeEntityType')} labelWidth="sm">
                  <input
                    value={form.scope_entity_type}
                    onChange={(e) => setForm((p) => ({ ...p, scope_entity_type: e.target.value }))}
                    autoCapitalize="off"
                    className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </FormRow>
              </>
            )}
            <div className="pt-1">
              <ToggleSwitch label={t('payment.policies.smartRouting')} checked={form.use_smart_routing} onChange={(v) => setForm((p) => ({ ...p, use_smart_routing: v }))} />
            </div>
            <div className="pt-1">
              <ToggleSwitch label={t('payment.policies.require3ds')} checked={form.require_3ds} onChange={(v) => setForm((p) => ({ ...p, require_3ds: v }))} />
            </div>
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('payment.policies.deleteConfirmTitle')}
        destructive
        isConfirming={store.isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
});

export default PoliciesTab;
